/**
 * TECH-JOB-SCREEN.md §3f — on-site report copy disposition (wrap-up).
 *
 * Renders only on jobs whose water provider carries the send flag (City of
 * Plano). Complete Job waits for one of the outcomes:
 *
 *   [Email it]         — contact name + email, ad-hoc (NEVER saved as a
 *                        contact). One branded email with every completed
 *                        city report form from this visit. §3f amendment:
 *                        vendor-WO jobs get an include picker so the same
 *                        email can carry the completed work order.
 *   [Text it]          — dormant until the org's A2P approval flips the
 *                        server flag; the parent passes smsEnabled.
 *   [I left a paper copy] — contact name.
 *   [No one available] — required note.
 *
 * Every outcome POSTs /api/jobs/:id/onsite-report-copy, which records the
 * disposition + a named Activity note server-side.
 */
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { CheckCircle, Loader2, Mail, MessageSquare, FileText, UserX, Send } from "lucide-react";

export interface OnsiteReportDisposition {
    outcome: "email" | "paper" | "no_one" | "text";
    name?: string | null;
    email?: string | null;
    phone?: string | null;
    note?: string | null;
    included_work_order?: boolean;
}

type Outcome = OnsiteReportDisposition["outcome"];

export function dispositionSummary(d: OnsiteReportDisposition): string {
    switch (d.outcome) {
        case "email":
            return `Emailed ${d.included_work_order ? "reports + work order" : "report copy"} to ${d.name ?? "contact"}${d.email ? ` at ${d.email}` : ""}`;
        case "text":
            return `Texted report copy to ${d.name ?? "contact"}${d.phone ? ` at ${d.phone}` : ""}`;
        case "paper":
            return `Left a paper copy with ${d.name ?? "contact"}`;
        case "no_one":
            return `No one available${d.note ? ` — ${d.note}` : ""}`;
    }
}

export function OnsiteReportCopySection({
    jobId,
    providerLabel,
    smsEnabled,
    canIncludeWorkOrder,
    disposition,
    onRecorded,
}: {
    jobId: string;
    providerLabel: string;
    smsEnabled: boolean;
    /** §3f amendment: vendor-WO job with a filled work order on file. */
    canIncludeWorkOrder: boolean;
    disposition: OnsiteReportDisposition | null;
    onRecorded: (d: OnsiteReportDisposition) => void;
}) {
    const [mode, setMode] = useState<Outcome | null>(null);
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [note, setNote] = useState("");
    const [includeWorkOrder, setIncludeWorkOrder] = useState(false);

    const record = useMutation({
        mutationFn: (body: Record<string, unknown>) =>
            api.post<{ disposition: OnsiteReportDisposition }>(`/api/jobs/${jobId}/onsite-report-copy`, body),
        onSuccess: (res, vars) => {
            const d = (res?.disposition ?? vars) as OnsiteReportDisposition;
            onRecorded(d);
            setMode(null);
            // §7 item 3 — every send is a fresh entry: never carry the
            // recipient forward into the next open of the form.
            setName("");
            setEmail("");
            setIncludeWorkOrder(false);
            toast.success(
                d.outcome === "email"
                    ? "Report copy sent"
                    : d.outcome === "text"
                      ? "Report copy texted"
                      : "Recorded",
            );
        },
        onError: (e: unknown) => {
            const err = e as { body?: { error?: string } };
            const code = err?.body?.error;
            // Item 17: never a silent dead Send — the real reason, with the
            // paper-copy path spelled out as the escape hatch.
            toast.error(
                code === "REPORT_GENERATION_FAILED"
                    ? "Reports couldn't be generated — choose \"I left a paper copy\" or retry."
                    : code === "NO_REPORTS"
                      ? "No completed test results on this job yet — finish a device test first, or choose \"I left a paper copy\"."
                      : code === "REPORT_COPY_NOT_ALLOWED"
                        ? "This jurisdiction doesn't allow sending reports from site."
                        : "Could not record — try again.",
            );
        },
    });

    // §7 item 3 — after the wrap-up disposition is answered the send action
    // stays available: [Send report copy] always opens a FRESH email form
    // (recipient re-entered from scratch, never locked to the earlier
    // entry), regardless of the earlier choice (sent / paper / no one).
    // Each send appends its own Activity line + ledger entry server-side;
    // the jurisdiction gate is unchanged (non-Plano still 403s).
    const isResend = disposition != null;

    if (disposition && mode === null) {
        return (
            <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm space-y-2">
                <div className="flex items-center gap-2 font-medium text-emerald-700">
                    <CheckCircle className="h-4 w-4 shrink-0" /> Report copy handled
                </div>
                <p className="mt-0.5 text-foreground/80">{dispositionSummary(disposition)}</p>
                <Button
                    variant="outline"
                    size="sm"
                    className="w-full justify-start"
                    onClick={() => {
                        setName("");
                        setEmail("");
                        setIncludeWorkOrder(false);
                        setMode("email");
                    }}
                >
                    <Send className="h-4 w-4 mr-2" /> Send report copy
                </Button>
            </div>
        );
    }

    const trimmedName = name.trim();

    return (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 space-y-3">
            <p className="text-sm font-medium">
                {isResend ? "Send report copy" : `${providerLabel} requires a copy on site`}
            </p>
            {mode === null && (
                <div className="grid grid-cols-1 gap-2">
                    <Button variant="outline" className="justify-start" onClick={() => setMode("email")}>
                        <Mail className="h-4 w-4 mr-2" /> Email it
                    </Button>
                    {smsEnabled && (
                        <Button variant="outline" className="justify-start" onClick={() => setMode("text")}>
                            <MessageSquare className="h-4 w-4 mr-2" /> Text it
                        </Button>
                    )}
                    <Button variant="outline" className="justify-start" onClick={() => setMode("paper")}>
                        <FileText className="h-4 w-4 mr-2" /> I left a paper copy
                    </Button>
                    <Button variant="outline" className="justify-start" onClick={() => setMode("no_one")}>
                        <UserX className="h-4 w-4 mr-2" /> No one available
                    </Button>
                </div>
            )}
            {mode !== null && (
                <div className="space-y-2">
                    {mode !== "no_one" && (
                        <div className="space-y-1">
                            <label htmlFor="onsite-copy-name" className="text-xs font-medium">
                                Contact name
                            </label>
                            <Input
                                id="onsite-copy-name"
                                autoFocus
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                            />
                        </div>
                    )}
                    {mode === "email" && (
                        <div className="space-y-1">
                            <label className="text-xs font-medium">Email</label>
                            <Input
                                type="email"
                                inputMode="email"
                                autoComplete="off"
                                placeholder="name@company.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                            <p className="text-[11px] text-foreground/60">
                                Used once for this copy — not saved to the customer.
                            </p>
                        </div>
                    )}
                    {mode === "text" && (
                        <div className="space-y-1">
                            <label className="text-xs font-medium">Mobile number</label>
                            <Input
                                type="tel"
                                inputMode="tel"
                                autoComplete="off"
                                placeholder="(555) 555-5555"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                            />
                            <p className="text-[11px] text-foreground/60">
                                Used once for this copy — not saved to the customer.
                            </p>
                        </div>
                    )}
                    {mode === "email" && (
                        <div className="space-y-1">
                            <p className="text-xs font-medium">Send:</p>
                            <label className="flex items-center gap-2 text-sm">
                                <input type="checkbox" checked readOnly disabled className="h-4 w-4" />
                                Test reports (required)
                            </label>
                            {canIncludeWorkOrder && (
                                <label className="flex items-center gap-2 text-sm">
                                    <input
                                        type="checkbox"
                                        checked={includeWorkOrder}
                                        onChange={(e) => setIncludeWorkOrder(e.target.checked)}
                                        className="h-4 w-4"
                                    />
                                    Completed work order
                                </label>
                            )}
                        </div>
                    )}
                    {mode === "no_one" && (
                        <div className="space-y-1">
                            <label className="text-xs font-medium">What happened?</label>
                            <Textarea
                                autoFocus
                                rows={2}
                                placeholder="e.g. store closed, manager unreachable…"
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                            />
                        </div>
                    )}
                    <div className="flex gap-2 pt-1">
                        <Button variant="ghost" size="sm" onClick={() => setMode(null)}>
                            Back
                        </Button>
                        <Button
                            size="sm"
                            className="flex-1"
                            disabled={
                                record.isPending ||
                                (mode !== "no_one" && !trimmedName) ||
                                (mode === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) ||
                                (mode === "text" && phone.trim().length < 7) ||
                                (mode === "no_one" && !note.trim())
                            }
                            onClick={() => {
                                const body: Record<string, unknown> = { outcome: mode };
                                if (mode !== "no_one") body.name = trimmedName;
                                if (mode === "email") {
                                    body.email = email.trim();
                                    body.includeWorkOrder = canIncludeWorkOrder && includeWorkOrder;
                                }
                                if (mode === "text") body.phone = phone.trim();
                                if (mode === "no_one") body.note = note.trim();
                                record.mutate(body);
                            }}
                        >
                            {record.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                            {mode === "email" || mode === "text" ? "Send" : "Record"}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
